import { AuthService } from '../auth/auth.service';

import { HttpClient } from '@angular/common/http';
import {
  computed,
  effect,
  inject,
  Injectable,
  Injector,
  signal,
  untracked,
} from '@angular/core';

import { environment } from '../../environments/environment';

import { PARAMETERS } from './all-parameters';
import {
  colorMappingCond,
  colorMappingPigeon,
  colorMappingTT,
  getCleanedWW,
  getGmaAlarmColor,
  gmaAlarmColors,
  gmaPrecipitationTypeMapping,
  transformWebcamUrl,
} from './constants/constants';
import { Moment } from 'moment';
import moment from 'moment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export enum StationType {
  gma = 'gma',
  synop = 'synop',
}

export interface StationData {
  stations: [
    {
      stationId: string;
      timeseries: {
        timeItems: [{}];
      };
    },
  ];
}

export interface StationInfo {
  stationId: number;
  name: string;
  distance?: number;
  mosId?: number;
  type?: string;
  latitude?: number;
  longitude?: number;
  icao?: string;
  observationStatus?: string;
  elevation?: number;
  gmaAlarm?: boolean;
}

export interface StationInfoRequest {
  stations: {
    stationItems: Array<StationInfo>;
  };
}

@Injectable()
export class SmwService {
  private http = inject(HttpClient);
  private auth = inject(AuthService);
  private injector = inject(Injector);

  private urlStationSearch = environment.url.bff + '/station/search/';
  private urlSmw = environment.url.bffV3 + '/smw/';
  private urlWebcam = environment.url.bff + '/app/webcam/';
  greenTableDesign = signal(false);
  private urlSmwBffV3 = environment.url.bffV3 + '/smw/';

  allParameters = PARAMETERS;

  cardParameterGroups = {
    gmaAlarm: ['gmaAlarm'],
    tt: ['tt'],
    td: ['td'],
    tg0: ['tg0'],
    t5: ['t5'],
    freezing_tt: ['freezing_tt'],
    road: ['road_condition', 'road_tt'],
    wind: ['dd', 'ff'],
    rh: ['rh'],
    // rrr: ['rrr']
  };

  public gmaAlarmText = {
    S: 'snow',
    E: 'ice',
    N: 'wet',
    R: 'rime',
    B: 'ground',
    0: 'noAlarm',
  };

  public gmaAlarmColors = gmaAlarmColors;

  public mapLayers = [
    {
      name: 'radar',
      style: 'connybruhn/ckcyi11261klk1iqvvrqowu4b',
    },
    {
      name: 'street',
      style: 'connybruhn/ckmb16v2ejg2h17lji448usbr',
    },
    {
      name: 'sat',
      style: 'connybruhn/ckmbuv4ph3bhn17ql98o3b4st',
    },
  ];

  activeMapLayer = signal<{
    name: string;
    style: string;
  }>(undefined);

  public readonly getActiveMapLayer = computed(() => {
    return this.activeMapLayer() ?? this.mapLayers[0];
  });

  constructor() {
    this.listenToActiveMapLayerChanges();
  }

  private listenToActiveMapLayerChanges() {
    effect(
      () => {
        if (this.activeMapLayer()) {
          return;
        }

        untracked(() => {
          const savedMapLayer = localStorage.getItem('mapLayer');

          const layer =
            this.mapLayers.find(m => m.name === savedMapLayer) ??
            this.mapLayers[0];

          this.activeMapLayer.set(layer);
        });
      },
      { injector: this.injector }
    );
  }

  getLatestDataForStations(stationsIds: string[]): Observable<any> {
    const fullUrl = this.urlSmw + 'latest?stations=' + stationsIds.join(',');
    return this.http.get(fullUrl).pipe(
      map(data => {
        const latestSmwData = {};
        if (data.hasOwnProperty('stations')) {
          for (const stationData of data['stations']) {
            const stationId = stationData['stationId'];
            const obsData = {};
            if (
              stationData.hasOwnProperty('timeseries') &&
              stationData['timeseries'].hasOwnProperty('timeItems') &&
              stationData['timeseries']['timeItems'].length > 0
            ) {
              const timeItem = stationData['timeseries']['timeItems'][0];
              obsData['dtg'] = timeItem['dtg'];
              for (const col in timeItem.weatherparameters) {
                if (timeItem.weatherparameters.hasOwnProperty(col)) {
                  for (const key in timeItem.weatherparameters[col]) {
                    if (timeItem.weatherparameters[col].hasOwnProperty(key)) {
                      obsData[key] = timeItem.weatherparameters[col][key];
                    }
                  }
                }
              }
            }
            if (this.isGmaAlarmForStationOrdered(stationId, false)) {
              const gmaAlarm = this.getGmaAlarm(obsData);
              if (gmaAlarm !== 0) {
                obsData['gmaAlarm'] = gmaAlarm;
              }
            }
            latestSmwData[stationId] = obsData;
          }
        }
        // Backend may omit stations it has no recent data for - the
        // overview template relies on every requested station having at
        // least an empty entry (it treats {} as the "no data" state).
        for (const stationId of stationsIds) {
          if (!latestSmwData.hasOwnProperty(stationId)) {
            latestSmwData[stationId] = {};
          }
        }
        return latestSmwData;
      })
    );
  }

  latestParameterGroups(latestSmwData): any {
    const parameterGroups = {};
    for (const parameterGroup of Object.keys(this.cardParameterGroups)) {
      const parameters = [];
      for (const parameter of this.cardParameterGroups[parameterGroup]) {
        if (latestSmwData.hasOwnProperty(parameter)) {
          parameters.push(parameter);
        }
      }
      if (parameters.length > 0) {
        parameterGroups[parameterGroup] = parameters;
      }
    }
    return parameterGroups;
  }

  getSmwData(stationId, dtgStart?: Moment, dtgEnd?: Moment): Observable<any> {
    let fullUrl = this.urlSmwBffV3 + stationId;
    let earliestDtg = moment().subtract(24, 'hours');
    if (dtgStart && dtgEnd) {
      earliestDtg = dtgStart;
      const from = dtgStart.utc().format('YYYY-MM-DDTHH:00:00') + 'Z';
      const to = dtgEnd.utc().format('YYYY-MM-DDTHH:00:00') + 'Z';
      fullUrl += '?dtgStart=' + from + '&dtgEnd=' + to;
    }
    return this.http.get<StationData>(fullUrl).pipe(
      map(data => {
        if (!data || !data.hasOwnProperty('stations')) {
          return null;
        }
        const timeArray = [];
        const smwData = [];
        for (const key of Object.keys(data.stations[0].timeseries.timeItems)) {
          timeArray.push(data.stations[0].timeseries.timeItems[key]);
        }
        for (const dataset of timeArray) {
          const datasetObject: any = {};
          datasetObject['dtg'] = dataset.dtg;
          if (moment(dataset.dtg).isBefore(earliestDtg)) {
            continue;
          }
          for (const col of Object.keys(dataset.weatherparameters)) {
            for (const key of Object.keys(dataset.weatherparameters[col])) {
              datasetObject[key] =
                dataset.weatherparameters[col][key] !== -9999
                  ? dataset.weatherparameters[col][key]
                  : null;
            }
          }
          if (this.isGmaAlarmForStationOrdered(stationId, dtgStart !== null)) {
            datasetObject['gmaAlarm'] = this.getGmaAlarm(datasetObject);
          }
          smwData.push(datasetObject);
        }
        return smwData;
      })
    );
  }

  getWebcamStatus(stationsIds: string[]): Observable<any> {
    const fullUrl = this.urlWebcam + 'state?idList=' + stationsIds.join(',');
    return this.http.get(fullUrl).pipe(
      map(data => {
        const webcamStatus = {};
        for (const dataset of data['responses']) {
          const stationId = dataset['stationId'];
          const available = dataset['status'] === 'AVAILABLE';
          webcamStatus[stationId] = available;
        }
        return webcamStatus;
      })
    );
  }

  getWebcamImages(stationId, limit: number): Observable<any[]> {
    const fullUrl = this.urlWebcam + 'images/' + stationId;

    return this.http.get<any>(fullUrl).pipe(
      map(data => {
        // Sicherer Zugriff auf die Liste
        const pictureList = data?.pictureList || [];
        if (!data || !data.pictureList) {
          return [];
        }
        return [...pictureList]
          .sort((a, b) => (a.dtg < b.dtg ? 1 : -1))
          .slice(0, limit);
      })
    );
  }

  public colorMappingCond(value: number): string {
    return colorMappingCond(value, this.greenTableDesign());
  }

  public colorMappingTT(value: number): string {
    return colorMappingTT(value, this.greenTableDesign());
  }

  public colorMappingTT_old(value: number): string {
    let color = this.greenTableDesign() ? 'table-green' : '';

    if (!value && value !== 0) {
      return color;
    }
    if (value <= 0.05) {
      color = 'table-danger';
    } else if (value <= 1.04) {
      color = 'table-orange';
    } else if (value <= 3.04) {
      color = 'table-warning';
    }
    return color;
  }

  public colorMappingPigeon(parameter: string, value: number): string {
    return colorMappingPigeon(parameter, value);
  }

  public transformWebcamUrl(urlIn: string): string {
    return transformWebcamUrl(urlIn);
  }

  // MAPPING.WW.UNKNOWN,WHITE_DEW

  gmaPrecipitationTypeMapping(value: number, stationSource): string {
    return gmaPrecipitationTypeMapping(value, stationSource);
  }

  getCleanedWW(ww: number): number {
    return getCleanedWW(ww);
  }

  isGmaAlarmForStationOrdered(
    stationId: string,
    alsoInArchive: boolean
  ): boolean {
    const customerDocument = this.auth.getCustomerDocument();
    for (const egwGmaProduct of customerDocument.egwGmaProducts) {
      if (
        this.auth.isProductActive(
          'egwGmaProducts',
          egwGmaProduct.id,
          alsoInArchive
        )
      ) {
        for (const station of egwGmaProduct.stations) {
          if (station.stationId === stationId) {
            return true;
          }
        }
      }
    }
    return false;
  }

  public getGmaAlarm(datasetOriginal: any): string | number {
    const dataset = {};
    for (const key in datasetOriginal) {
      if (key === 'dtg' || datasetOriginal[key] === null) {
        dataset[key] = datasetOriginal[key];
      } else if (typeof datasetOriginal[key] === 'string') {
        dataset[key] = Number(datasetOriginal[key]);
      } else {
        dataset[key] = datasetOriginal[key];
      }
    }
    // Schneefall: Fahrbahntemperatur< 0.5°C +Niederschlagsmenge> 0 mm/h +Niederschlagsart= Schnee
    if (
      dataset.hasOwnProperty('r_type') &&
      dataset['r_type'] === 70 &&
      dataset.hasOwnProperty('road_tt') &&
      dataset['road_tt'] < 0.5 &&
      dataset.hasOwnProperty('rrr') &&
      dataset['rrr'] > 0
    ) {
      return 'S';
    }
    // Eisregen: Fahrbahntemperatur< 0.5°C +Niederschlagsmenge> Omm/h +Niederschlagsart= Regen)
    if (
      dataset.hasOwnProperty('rrr') &&
      dataset['rrr'] > 0 &&
      dataset.hasOwnProperty('r_type') &&
      dataset['r_type'] === 60 &&
      ((dataset.hasOwnProperty('road_tt') && dataset['road_tt'] < 0.5) ||
        (dataset.hasOwnProperty('road_tt') &&
          dataset.hasOwnProperty('freezing_tt') &&
          dataset['road_tt'] < dataset['freezing_tt']))
    ) {
      return 'E';
    }
    // Kritische Nässe: Fahrbahntemperatur< 0°C + kein Niederschlag+ Fahrbahnzustand ist nicht
    // (trocken, kühl, keine Daten, defekt, undefinierbar)
    const conditionValuesDry = [0, 1, 999];
    if (
      dataset.hasOwnProperty('road_tt') &&
      dataset['road_tt'] < 0 &&
      //      && dataset.hasOwnProperty('rrr') && dataset['rrr'] === 0
      dataset.hasOwnProperty('road_condition') &&
      conditionValuesDry.indexOf(dataset['road_condition']) < 0
    ) {
      return 'N';
    }
    // Reif: Fahrbahntemperatur< 0°C +Fahrbahntemperatur< Taupunkt
    if (
      dataset.hasOwnProperty('road_tt') &&
      dataset['road_tt'] < 0 &&
      dataset.hasOwnProperty('td') &&
      dataset['road_tt'] < dataset['td']
    ) {
      return 'R';
    }
    // Bodenfrost: Fahrbahntemperatur< 0°C
    if (dataset.hasOwnProperty('road_tt') && dataset['road_tt'] < 0) {
      return 'B';
    }
    return 0;
    // return dataset.hasOwnProperty('tt') ? 'S' : 0; // 0;
  }

  public setActiveMapLayer(mapLayerName: string): void {
    for (const mapLayer of this.mapLayers) {
      if (mapLayer.name === mapLayerName) {
        this.activeMapLayer.set(mapLayer);
      }
    }
    const savedMapLayer = localStorage.getItem('mapLayer');
    if (savedMapLayer !== mapLayerName) {
      localStorage.setItem('mapLayer', mapLayerName);
    }
  }

  getGmaAlarmColor(stationDataByAlarmParameter: any) {
    return getGmaAlarmColor(stationDataByAlarmParameter);
  }
}
